import React, { createContext, useEffect, useState } from "react";
import request from "../api/request";
import { UserRole } from "../const/enum";
import { BackendAPI } from "../const/api";

export interface User {
  id: number;
  username: string;
  role: UserRole;
  name: string | null;
  needUpdatePassword: boolean;
}

export interface AuthContextType {
  user: User | null;
  login: (username: string, password: string) => Promise<void>;
  register: (
    username: string,
    password: string,
    email: string,
    phone: string,
    name: string,
    abbreviation: string,
    sale: string,
    address: string,
  ) => Promise<void>;
  logout: () => void;
  isAdmin: () => boolean;
  isBA: () => boolean;
  isCustomer: () => boolean;
  isWarehouse: () => boolean;
  isFinance: () => boolean;
  isPurchaser: () => boolean;
  isSalesperson: () => boolean;
  isPartner: () => boolean;
  isSalesScopeRole: () => boolean;
  isInitialized: boolean;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);

  const [isInitialized, setIsInitialized] = useState<boolean>(false); // 新增：初始化状态

  // Provider初次渲染时先初始化用户认证信息
  useEffect(() => {
    setIsInitialized(false); // 设置为未初始化
    if (localStorage.getItem("token")) {
      authUserInfo()
        .then((user) => {
          if (user) {
            setUser(user);
          }
          setIsInitialized(true); // 设置为已初始化
        })
        .catch((e) => {
          setIsInitialized(true);
          console.error("authUserInfo error", e);
        });
    } else {
      setIsInitialized(true); // 设置为已初始化
    }
  }, []);

  const authUserInfo = async () => {
    const res = await request.get(BackendAPI.userInfo);
    return res.data;
  };

  const login = async (username: string, password: string) => {
    const response = await request.post(BackendAPI.userLogin, {
      name: username,
      password,
    });
    if (response.success) {
      // token
      localStorage.setItem("token", response.data.token);
      await authUserInfo().then((user) => {
        if (user) {
          setUser(user);
        }
        setIsInitialized(true); // 设置为已初始化
      });
    } else {
      throw new Error("登录失败, login无返回");
    }
  };

  const register = async (
    username: string,
    password: string,
    email: string,
    phone: string,
    name: string,
    abbreviation: string,
    sale: string,
    address: string,
  ) => {
    const response = await request.post(BackendAPI.userRegister, {
      username,
      password,
      email,
      phone,
      name,
      abbreviation,
      sale,
      address,
    });
    if (response.success) {
      // token
      localStorage.setItem("token", response.data.token);
    } else {
      throw new Error("注册失败");
    }
  };

  const logout = () => {
    localStorage.removeItem("token");
    setUser(null);
  };

  const isAdmin = () => {
    if (!user || !user.role) return false;
    return UserRole[user.role] === UserRole.ADMIN;
  };
  const isBA = () => {
    if (!user || !user.role) return false;
    return UserRole[user.role] === UserRole.BA;
  };
  const isCustomer = () => {
    if (!user || !user.role) return false;
    return UserRole[user.role] === UserRole.CUSTOMER;
  };
  const isWarehouse = () => {
    if (!user || !user.role) return false;
    return UserRole[user.role] === UserRole.WAREHOUSE_MANAGER;
  };

  const isFinance = () => {
    if (!user || !user.role) return false;
    return UserRole[user.role] === UserRole.FINANCE;
  };

  const isPurchaser = () => {
    if (!user || !user.role) return false;
    return UserRole[user.role] === UserRole.PURCHASER;
  };

  const isSalesperson = () => {
    if (!user || !user.role) return false;
    return UserRole[user.role] === UserRole.SALESPERSON;
  };

  const isPartner = () => {
    if (!user || !user.role) return false;
    return UserRole[user.role] === UserRole.PARTNER;
  };

  const isSalesScopeRole = () => {
    return isSalesperson() || isPartner();
  };

  const value: AuthContextType = {
    user,
    login,
    register,
    logout,
    isAdmin,
    isBA,
    isCustomer,
    isWarehouse,
    isFinance,
    isPurchaser,
    isSalesperson,
    isPartner,
    isSalesScopeRole,
    isInitialized,
  };

  return (
    <AuthContext.Provider
      value={value}
    >
      {children}
    </AuthContext.Provider>
  );
};
